import {
  Card,
  HorizontalBarStacked,
  HorizontalBarStackedRow,
  HorizontalBarStackedSegment,
} from '@payaca/ambrosia';
import { FC, useMemo } from 'react';
import {
  currencyLabel,
  humanizeCurrency,
} from '../../../../../helpers/currency';

type BreakdownItem = {
  actual: {
    currency: {
      code: string;
      exponent: number;
    };
    value: number;
  };
  forecast: {
    currency: {
      code: string;
      exponent: number;
    };
    value: number;
  };
  committed?: {
    value: number;
  } | null;
};
export type Props = {
  className?: string;
  profitData?: {
    revenue: BreakdownItem;
    materials: BreakdownItem;
    labour: BreakdownItem;
    profit: BreakdownItem;
  };
};

type SegmentState = 'done' | 'committed' | 'stillToGo';

const segmentStates: SegmentState[] = ['done', 'committed', 'stillToGo'];

const segmentColours: Record<SegmentState, string> = {
  done: '#9E77ED',
  committed: '#DBCDF8',
  stillToGo: '#6941C6',
};

type ProfitTone = 'gain' | 'loss';

const profitColours: Record<ProfitTone, { solid: string; striped: string }> = {
  gain: { solid: '#ccedc5', striped: '#316e27' },
  loss: { solid: '#fecaca', striped: '#ba1b1b' },
};

const profitTone = (value: number): ProfitTone => (value < 0 ? 'loss' : 'gain');

// The striped segment shows where profit is heading: profit still to make, or
// a loss forecast to deepen. A loss with a positive forecast is drawn either
// side of zero: the loss left, the forecast right. A loss forecast to shrink is
// solid to the forecast, with the recovery striped out to the actual. A
// positive forecast is always green, even beside a loss.
const profitSegments = (
  actual: number,
  forecast: number
): HorizontalBarStackedSegment[] => {
  const { solid, striped } = profitColours[profitTone(actual)];
  const segments: HorizontalBarStackedSegment[] = [
    { label: 'Actual', value: actual, colour: solid, tooltipValue: actual },
    { label: '', value: 0, colour: solid },
  ];

  if (actual < 0 && forecast < actual) {
    return [
      ...segments,
      {
        label: '',
        value: forecast - actual,
        colour: striped,
        striped: true,
      },
    ];
  }

  if (actual < forecast && forecast < 0) {
    return [
      { label: 'Actual', value: forecast, colour: solid, tooltipValue: actual },
      { label: '', value: 0, colour: solid },
      {
        label: '',
        value: actual - forecast,
        colour: striped,
        striped: true,
      },
    ];
  }

  if (forecast <= Math.max(actual, 0)) {
    return segments;
  }

  return [
    ...segments,
    {
      label: '',
      value: actual < 0 ? forecast : forecast - actual,
      colour: profitColours.gain.striped,
      striped: true,
    },
  ];
};

const segmentLabels: Record<
  'revenue' | 'materials' | 'labour',
  Record<SegmentState, string>
> = {
  revenue: {
    done: 'Paid',
    committed: 'Invoiced, not yet paid',
    stillToGo: 'Still to invoice',
  },
  materials: {
    done: 'Bought',
    committed: 'Ordered, not yet received',
    stillToGo: 'Still to buy',
  },
  labour: {
    done: 'Tracked',
    committed: 'Booked, not yet worked',
    stillToGo: 'Still to book',
  },
};

const toAmounts = (item?: BreakdownItem): Record<SegmentState, number> => {
  const done = item?.actual.value ?? 0;
  const committed = item?.committed?.value ?? 0;
  const forecast = item?.forecast.value ?? 0;

  return {
    done,
    committed,
    stillToGo: Math.max(0, forecast - done - committed),
  };
};

const toSegments = (
  amounts: Record<SegmentState, number>,
  labels: Record<SegmentState, string>,
  sign: 1 | -1
): HorizontalBarStackedSegment[] =>
  segmentStates.map((state) => ({
    label: labels[state],
    value: sign * amounts[state],
    colour: segmentColours[state],
    striped: state === 'stillToGo',
  }));

const ProfitBreakdown: FC<Props> = (props) => {
  const { className, profitData } = props;

  const currencyCode = useMemo(() => {
    const currency =
      profitData &&
      Object.values(profitData).find((item) => item.actual.currency);
    return currency?.actual.currency?.code;
  }, [profitData]);

  const format = (val: number) =>
    humanizeCurrency({
      value: val,
      ...(currencyCode ? { currency: { code: currencyCode } } : {}),
    });

  const forecastKey = (label: string, item?: BreakdownItem) => ({
    label,
    value: format(item?.forecast.value ?? 0),
    colour: '#e0e0e0',
    outlined: true,
  });

  const profit = {
    actual: profitData?.profit.actual.value ?? 0,
    forecast: profitData?.profit.forecast.value ?? 0,
  };

  const forecastTone = profitTone(profit.forecast);

  const rows: HorizontalBarStackedRow[] = [
    {
      label: 'Revenue',
      segments: toSegments(
        toAmounts(profitData?.revenue),
        segmentLabels.revenue,
        1
      ),
      extraTooltipKeys: [forecastKey('Project value', profitData?.revenue)],
    },
    {
      label: 'Materials',
      segments: toSegments(
        toAmounts(profitData?.materials),
        segmentLabels.materials,
        -1
      ),
      extraTooltipKeys: [forecastKey('Forecast', profitData?.materials)],
    },
    {
      label: 'Labour',
      segments: toSegments(
        toAmounts(profitData?.labour),
        segmentLabels.labour,
        -1
      ),
      extraTooltipKeys: [forecastKey('Forecast', profitData?.labour)],
    },
    {
      label: 'Profit',
      segments: profitSegments(profit.actual, profit.forecast),
      extraTooltipKeys: [
        {
          label: 'Forecast',
          value: format(profit.forecast),
          colour: profitColours[forecastTone].striped,
          striped: true,
        },
      ],
    },
  ];

  return (
    <Card className={className} sizeVariant="lg">
      <HorizontalBarStacked
        rows={rows}
        valueFormatter={(val) => currencyLabel(val, currencyCode, false)}
        tooltipFormatter={format}
        height={200}
        width={'100%'}
      />
    </Card>
  );
};

export default ProfitBreakdown;
